/**
 * MQTT — Broker, uživatelé a ACL
 * EMQX management, tenant izolace, fleet komunikace
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatCard, StatsGrid } from '../../components/ui/Stats';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Tab, TabList, TabPanel, Tabs } from '../../components/ui/Tabs';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { StatusDot } from '../../components/ui/StatusDot';
import { Modal } from '../../components/ui/Modal';
import { SearchInput } from '../../components/ui/SearchInput';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Label } from '../../components/ui/Label';
import {
  AlertCircle,
  CheckCircle2,
  Code,
  Copy,
  Globe,
  Key,
  Plus,
  Radio,
  RefreshCw,
  Server,
  Shield,
  Terminal,
  Trash2,
  Users,
} from 'lucide-react';
import { themedConfirm, themedPrompt } from '../../lib/themed-dialogs';
import {
  clientContactIso,
  clientFamiliarName,
  mqttClientId,
  type MqttBridgeRow,
  type MqttClientRow,
  type MqttSubRow,
} from '../../lib/mqtt-view';
import { MqttRelations, MqttSubscriptions } from './mqtt/panels';
import { useMeshPeers } from '../../widgets/NodePicker';
import { peerMatchesKey, resolveNodeDisplayLabel } from '../../widgets/node-targets';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MqttConfig {
  emqx_url: string;
  broker_url: string;
  broker_host: string;
  emqx_api_key: string;
  configured: boolean;
  emqx_running: boolean;
}

interface MqttStatus {
  ok: boolean;
  configured: boolean;
  emqx_url: string;
  broker_host: string;
  emqx_api_key?: string;
  emqx_running?: boolean;
  systemd?: string;
  broker_url?: string;
  brokers?: unknown;
  stats?: unknown;
}

interface MqttClient {
  clientid?: string;
  client_id?: string;
  username: string;
  ip_address: string;
  port: number;
  protocol: string;
  connected?: boolean;
  connected_at: string;
  disconnected_at?: string;
  is_bridge: boolean;
  subscriptions_cnt?: number;
}

interface MqttUser {
  user_id: string;
  is_superuser: boolean;
  created_at: string;
}

interface AclRule {
  username: string;
  rules: Array<{
    permission: string;
    action: string;
    topic: string;
  }>;
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

function ServiceBadge({ running }: { running: boolean }) {
  const { t } = useTranslation();
  return (
    <span className='inline-flex items-center gap-1.5 text-xs font-medium'>
      <StatusDot status={running ? 'running' : 'error'} />
      <span className={running ? 'text-emerald-400' : 'text-red-400'}>
        {running ? t('mqtt.statusRunning') : t('mqtt.statusStopped')}
      </span>
    </span>
  );
}

// ─── KeyBadge ────────────────────────────────────────────────────────────────

function KeyBadge({ apiKey }: { apiKey: string }) {
  const { t } = useTranslation();
  return (
    <div className='flex items-center gap-2'>
      <code className='font-mono text-xs text-gray-400 bg-gray-900 px-2 py-1 rounded border border-gray-800'>
        {apiKey.slice(0, 8)}...
      </code>
      <Button
        variant='ghost'
        size='icon'
        className='h-6 w-6'
        title={t('mqtt.copyTooltip')}
        onClick={() => {
          navigator.clipboard.writeText(apiKey);
          toast.success(t('mqtt.apiKeyCopied'));
        }}
      >
        <Copy size={12} />
      </Button>
    </div>
  );
}

// ─── ConfigForm ──────────────────────────────────────────────────────────────

function ConfigForm(
  { config, onSave }: { config: MqttConfig | null; onSave: (data: Partial<MqttConfig>) => void },
) {
  const { t } = useTranslation();
  const [emqxUrl, setEmqxUrl] = useState(config?.emqx_url ?? '');
  const [emqxApiKey, setEmqxApiKey] = useState('');
  const [brokerUrl, setBrokerUrl] = useState(config?.broker_url ?? '');
  const [brokerHost, setBrokerHost] = useState(config?.broker_host ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      emqx_url: emqxUrl,
      emqx_api_key: emqxApiKey || config?.emqx_api_key,
      broker_url: brokerUrl,
      broker_host: brokerHost,
    });
  };

  if (!config) {
    return (
      <div className='p-8 text-center'>
        <AlertCircle className='mx-auto text-gray-600 mb-3' size={32} />
        <h3 className='text-sm font-medium text-gray-400'>{t('mqtt.notConfiguredTitle')}</h3>
        <p className='text-xs text-gray-600 mt-1'>
          {t('mqtt.installHintPre')} <code className='font-mono text-gray-500'>mqtt_status</code>{' '}
          {t('mqtt.installHintPost')}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className='space-y-6'>
      <div className='grid gap-6 md:grid-cols-2'>
        <div className='space-y-2'>
          <Label htmlFor='emqxUrl'>{t('mqtt.emqxRestApiUrlLabel')}</Label>
          <Input
            id='emqxUrl'
            value={emqxUrl}
            onChange={(e) => setEmqxUrl(e.target.value)}
            placeholder='http://127.0.0.1:18083'
            className='font-mono text-xs'
          />
          <p className='text-[10px] text-gray-500'>
            {t('mqtt.emqxUrlDefaultHint')}
          </p>
        </div>

        <div className='space-y-2'>
          <Label htmlFor='emqxApiKey'>{t('mqtt.emqxApiKeyLabel')}</Label>
          <div className='flex gap-2'>
            <Input
              id='emqxApiKey'
              type='password'
              value={emqxApiKey}
              onChange={(e) => setEmqxApiKey(e.target.value)}
              placeholder='key:secret'
              className='font-mono text-xs flex-1'
            />
          </div>
          <p className='text-[10px] text-gray-500'>
            {t('mqtt.emqxApiKeyHint')}
          </p>
        </div>

        <div className='space-y-2'>
          <Label htmlFor='brokerUrl'>{t('mqtt.publicMqttUrlLabel')}</Label>
          <Input
            id='brokerUrl'
            value={brokerUrl}
            onChange={(e) => setBrokerUrl(e.target.value)}
            placeholder='mqtts://acm.example.com:8883'
            className='font-mono text-xs'
          />
        </div>

        <div className='space-y-2'>
          <Label htmlFor='brokerHost'>{t('mqtt.brokerHostnameLabel')}</Label>
          <Input
            id='brokerHost'
            value={brokerHost}
            onChange={(e) => setBrokerHost(e.target.value)}
            placeholder='acm.example.com'
          />
        </div>
      </div>

      <div className='flex items-center justify-end gap-2 pt-4 border-t border-gray-800'>
        <Button type='submit' variant='primary' icon={<CheckCircle2 size={14} />}>
          {t('mqtt.saveConfigButton')}
        </Button>
      </div>
    </form>
  );
}

// ─── UserTable ───────────────────────────────────────────────────────────────

function UserTable(
  { users, onDelete, onCreate }: {
    users: MqttUser[];
    onDelete: (username: string) => void;
    onCreate: () => void;
  },
) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filtered = users.filter((u) => u.user_id.toLowerCase().includes(search.toLowerCase()));

  if (users.length === 0) {
    return (
      <EmptyState
        icon={<Users size={24} />}
        title={t('mqtt.noUsersTitle')}
        description={t('mqtt.noUsersDescription')}
        action={
          <Button variant='primary' size='sm' onClick={onCreate} icon={<Plus size={14} />}>
            {t('mqtt.createUserAction')}
          </Button>
        }
      />
    );
  }

  return (
    <div className='space-y-4'>
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder={t('mqtt.searchUsersPlaceholder')}
        resultCount={filtered.length}
        totalCount={users.length}
      />

      <div className='rounded-xl border border-gray-800 overflow-hidden bg-gray-900/20'>
        <table className='w-full text-left text-sm'>
          <thead className='bg-gray-900/40 border-b border-gray-800'>
            <tr>
              <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                {t('mqtt.colUser')}
              </th>
              <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                {t('mqtt.colSuperuser')}
              </th>
              <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                {t('mqtt.colCreated')}
              </th>
              <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right'>
                {t('mqtt.colActions')}
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-800'>
            {filtered.map((user) => (
              <tr key={user.user_id} className='hover:bg-gray-800/30 transition-colors'>
                <td className='px-4 py-3 font-medium text-white'>
                  {user.user_id}
                </td>
                <td className='px-4 py-3'>
                  <Badge
                    variant={user.is_superuser ? 'warning' : 'secondary'}
                    className={user.is_superuser ? 'bg-amber-900/30 text-amber-400' : ''}
                  >
                    {user.is_superuser ? t('mqtt.yes') : t('mqtt.no')}
                  </Badge>
                </td>
                <td className='px-4 py-3 text-gray-400'>
                  {new Date(user.created_at).toLocaleDateString('cs-CZ')}
                </td>
                <td className='px-4 py-3 text-right'>
                  <Button
                    variant='danger'
                    size='sm'
                    icon={<Trash2 size={10} />}
                    onClick={() =>
                      onDelete(user.user_id)}
                  >
                    {t('mqtt.deleteAction')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className='p-8 text-center text-gray-500 text-sm'>
            {t('mqtt.noUsersFound')}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AclPreview ──────────────────────────────────────────────────────────────

function AclPreview({ rules }: { rules?: AclRule['rules'] }) {
  const { t } = useTranslation();
  if (!rules || rules.length === 0) {
    return (
      <div className='p-8 text-center'>
        <Shield size={24} className='mx-auto text-gray-600 mb-3' />
        <p className='text-sm font-medium text-gray-400'>{t('mqtt.noAclRulesTitle')}</p>
        <p className='text-xs text-gray-600 mt-1'>{t('mqtt.noAclRulesUserDescription')}</p>
      </div>
    );
  }

  return (
    <div className='rounded-xl border border-gray-800 bg-gray-900/20 overflow-hidden'>
      <div className='px-4 py-2 bg-gray-900/40 border-b border-gray-800'>
        <div className='flex items-center gap-2'>
          <Shield size={14} className='text-gray-400' />
          <span className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>
            {t('mqtt.aclRulesHeader')}
          </span>
        </div>
      </div>
      <div className='max-h-64 overflow-y-auto'>
        {rules.map((rule, idx) => (
          <div
            key={idx}
            className='px-4 py-3 text-xs border-b border-gray-800/50 last:border-0 flex items-start gap-2'
          >
            <span className={rule.permission === 'allow' ? 'text-emerald-400' : 'text-red-400'}>
              {rule.permission === 'allow' ? '✓' : '✗'}
            </span>
            <div className='flex-1'>
              <span className='font-mono text-gray-400 mr-2'>{rule.action.toUpperCase()}</span>
              <span className='font-mono text-blue-400'>{rule.topic}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

// ── ACL Patterns (MQTT content filter) ─────────────────────────────────────

interface AclPattern {
  severity: string;
  name: string;
  pattern: string;
}

function AclPatternsSection() {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [form, setForm] = useState({ severity: 'medium', name: '', pattern: '' });
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState<
    Array<{ name: string; severity: string; matched: boolean }>
  >([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importMode, setImportMode] = useState<'replace' | 'merge'>('replace');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['mqtt-acl-patterns'],
    queryFn: () =>
      api.mcp<{ patterns?: AclPattern[] }>('mqtt.mqtt_acl_patterns', { action: 'get' })
        .then((r) => r ?? { patterns: [] }),
    staleTime: 30_000,
  });
  const patterns: AclPattern[] = data?.patterns ?? [];

  const callTool = (args: Record<string, unknown>) =>
    api.mcp('mqtt.mqtt_acl_patterns', args);

  const saveMut = useMutation({
    mutationFn: (newPatterns: AclPattern[]) => callTool({ action: 'set', patterns: newPatterns }),
    onSuccess: () => {
      toast.success(t('mqtt.aclRulesSaved'));
      refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resetMut = useMutation({
    mutationFn: () => callTool({ action: 'reset' }),
    onSuccess: () => {
      toast.success(t('mqtt.resetToDefaultToast'));
      refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const validatePatterns = (
    raw: unknown,
  ): { ok: boolean; patterns?: AclPattern[]; error?: string } => {
    if (!Array.isArray(raw)) return { ok: false, error: t('mqtt.jsonMustBeArrayError') };
    const sevOk = ['critical', 'high', 'medium', 'low'];
    const out: AclPattern[] = [];
    for (let i = 0; i < raw.length; i++) {
      const r = raw[i] as Record<string, unknown>;
      if (!r || typeof r !== 'object') {
        return { ok: false, error: t('mqtt.itemNotObjectError', { index: i }) };
      }
      if (typeof r.name !== 'string' || !r.name) {
        return { ok: false, error: t('mqtt.itemMissingNameError', { index: i }) };
      }
      if (typeof r.pattern !== 'string' || !r.pattern) {
        return { ok: false, error: t('mqtt.itemMissingPatternError', { index: i }) };
      }
      if (typeof r.severity !== 'string' || !sevOk.includes(r.severity)) {
        return { ok: false, error: t('mqtt.itemInvalidSeverityError', { index: i }) };
      }
      try {
        new RegExp(r.pattern);
      } catch (e) {
        return {
          ok: false,
          error: t('mqtt.itemInvalidRegexError', { index: i, error: (e as Error).message }),
        };
      }
      out.push({ severity: r.severity, name: r.name, pattern: r.pattern });
    }
    return { ok: true, patterns: out };
  };

  const handleImport = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(importText);
    } catch (e) {
      toast.error(t('mqtt.invalidJsonError', { error: (e as Error).message }));
      return;
    }
    const v = validatePatterns(parsed);
    if (!v.ok) {
      toast.error(v.error!);
      return;
    }
    const merged = importMode === 'merge'
      ? [...patterns, ...v.patterns!.filter((np) => !patterns.some((p) => p.name === np.name))]
      : v.patterns!;
    saveMut.mutate(merged);
    setImportOpen(false);
    setImportText('');
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setImportText(text);
  };

  const handleExportDownload = () => {
    const json = JSON.stringify(patterns, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mqtt-acl-patterns-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t('mqtt.downloadedToast', { filename: a.download }));
  };

  const sevBadge = (s: string): 'error' | 'warning' | 'secondary' | 'default' => {
    if (s === 'critical') return 'error';
    if (s === 'high') return 'warning';
    if (s === 'medium') return 'secondary';
    return 'default';
  };

  const handleAdd = () => {
    if (!form.name || !form.pattern) {
      toast.error(t('mqtt.nameAndPatternRequired'));
      return;
    }
    try {
      new RegExp(form.pattern);
    } catch (e) {
      toast.error(t('mqtt.invalidRegexError', { error: (e as Error).message }));
      return;
    }
    saveMut.mutate([...patterns, { ...form }]);
    setForm({ severity: 'medium', name: '', pattern: '' });
    setAddOpen(false);
  };
  const handleDelete = (i: number) => saveMut.mutate(patterns.filter((_, idx) => idx !== i));
  const handleEdit = (i: number) => {
    setForm({ ...patterns[i] });
    setEditIdx(i);
    setAddOpen(true);
  };
  const handleSaveEdit = () => {
    if (editIdx === null) return;
    try {
      new RegExp(form.pattern);
    } catch (e) {
      toast.error(t('mqtt.invalidRegexError', { error: (e as Error).message }));
      return;
    }
    const upd = [...patterns];
    upd[editIdx] = { ...form };
    saveMut.mutate(upd);
    setEditIdx(null);
    setAddOpen(false);
    setForm({ severity: 'medium', name: '', pattern: '' });
  };

  const sevCounts = patterns.reduce((acc, p) => {
    acc[p.severity] = (acc[p.severity] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className='space-y-4'>
      {/* Info banner */}
      <Card className='border-blue-500/20 bg-blue-950/10'>
        <CardContent className='py-3'>
          <div className='flex items-start gap-3'>
            <Shield size={18} className='text-blue-400 mt-0.5 flex-shrink-0' />
            <div className='space-y-1 text-xs'>
              <p className='text-blue-300 font-semibold'>
                {t('mqtt.contentFilterTitle')}
              </p>
              <p className='text-blue-200/70'>
                {t('mqtt.contentFilterDescPre')}{' '}
                <code className='font-mono bg-black/30 px-1 rounded'>
                  acm/catalog/mqtt/acl_patterns
                </code>
                {t('mqtt.contentFilterDescMid')}{' '}
                <Badge variant='error' className='mx-1'>critical</Badge> {t('mqtt.orWord')}
                <Badge variant='warning' className='mx-1'>high</Badge> {t('mqtt.contentFilterDescPost')}
              </p>
              <p className='text-blue-200/50 text-[11px]'>
                {t('mqtt.relatedPre')} <span className='text-blue-300'>toolbox → Security Scan</span>{' '}
                {t('mqtt.relatedPost')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <div>
            <p className='text-sm text-white font-semibold'>
              {t('mqtt.aclRulesCountLabel', { count: patterns.length })}
            </p>
            <div className='flex gap-2 text-[11px] text-gray-500 mt-0.5'>
              {sevCounts.critical
                ? <span className='text-red-400'>● {sevCounts.critical} critical</span>
                : null}
              {sevCounts.high
                ? <span className='text-amber-400'>● {sevCounts.high} high</span>
                : null}
              {sevCounts.medium
                ? <span className='text-gray-400'>● {sevCounts.medium} medium</span>
                : null}
              {sevCounts.low ? <span className='text-gray-500'>● {sevCounts.low} low</span> : null}
            </div>
          </div>
        </div>
        <div className='flex gap-2 flex-wrap'>
          <RefreshButton onRefresh={() => refetch()} loading={isLoading} size='md' />
          <Button
            variant='secondary'
            size='sm'
            onClick={async () => {
              const input = await themedPrompt(t('mqtt.testPromptMessage'));
              if (!input) return;
              setTestInput(input);
              const results = patterns.map((p) => {
                try {
                  return {
                    name: p.name,
                    severity: p.severity,
                    matched: new RegExp(p.pattern).test(input),
                  };
                } catch {
                  return { name: p.name, severity: p.severity, matched: false };
                }
              });
              setTestResult(results);
            }}
          >
            {t('mqtt.testButton')}
          </Button>
          <Button variant='ghost' size='sm' onClick={() => setImportOpen(true)}>
            {t('mqtt.importButton')}
          </Button>
          <Button variant='ghost' size='sm' onClick={handleExportDownload}>
            {t('mqtt.exportButton')}
          </Button>
          <Button
            variant='ghost'
            size='sm'
            onClick={async () => {
              if (await themedConfirm(t('mqtt.resetConfirm'))) resetMut.mutate();
            }}
            loading={resetMut.isPending}
          >
            {t('mqtt.resetButton')}
          </Button>
          <Button
            variant='primary'
            size='sm'
            onClick={() => {
              setEditIdx(null);
              setForm({ severity: 'medium', name: '', pattern: '' });
              setAddOpen(true);
            }}
          >
            {t('mqtt.addButton')}
          </Button>
        </div>
      </div>

      {patterns.length === 0 && !isLoading
        ? (
          <EmptyState
            icon={<Shield size={24} />}
            title={t('mqtt.noAclRulesTitle')}
            description={t('mqtt.noAclRulesHint')}
          />
        )
        : (
          <Card>
            <CardContent className='p-0'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-gray-800 text-left text-xs text-gray-500 uppercase tracking-wider'>
                    <th className='px-4 py-3 w-28'>{t('mqtt.colSeverity')}</th>
                    <th className='px-4 py-3'>{t('mqtt.colName')}</th>
                    <th className='px-4 py-3'>{t('mqtt.colPattern')}</th>
                    <th className='px-4 py-3 w-20'></th>
                  </tr>
                </thead>
                <tbody>
                  {patterns.map((p, i) => (
                    <tr
                      key={i}
                      className='border-b border-gray-800/50 last:border-0 hover:bg-gray-800/20'
                    >
                      <td className='px-4 py-3'>
                        <Badge variant={sevBadge(p.severity)}>{p.severity}</Badge>
                      </td>
                      <td className='px-4 py-3 text-gray-300'>{p.name}</td>
                      <td className='px-4 py-3 font-mono text-xs text-gray-400 break-all'>
                        {p.pattern}
                      </td>
                      <td className='px-4 py-3'>
                        <div className='flex gap-1'>
                          <button
                            onClick={() => handleEdit(i)}
                            className='text-gray-600 hover:text-blue-400 p-1'
                            title={t('mqtt.editTooltip')}
                          >
                            ✎
                          </button>
                          <button
                            onClick={async () => {
                              if (await themedConfirm(t('mqtt.deleteConfirmSimple'))) handleDelete(i);
                            }}
                            className='text-gray-600 hover:text-red-400 p-1'
                            title={t('mqtt.deleteTooltip')}
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

      {testResult.length > 0 && (
        <Card className='border-amber-500/30'>
          <CardContent className='py-3'>
            <div className='flex items-center justify-between mb-2'>
              <span className='text-xs text-gray-500'>
                {t('mqtt.testPayloadLabel')} <span className='font-mono text-amber-400'>{testInput}</span>
              </span>
              <button
                onClick={() => setTestResult([])}
                className='text-gray-600 hover:text-white text-xs'
              >
                ✕
              </button>
            </div>
            <div className='space-y-1'>
              {testResult.filter((r) => r.matched).length === 0
                ? (
                  <p className='text-xs text-green-400'>
                    ✓ {t('mqtt.noPatternMatchedMessage')}
                  </p>
                )
                : (
                  testResult.filter((r) => r.matched).map((r, i) => (
                    <div key={i} className='flex items-center gap-2 text-xs'>
                      <Badge
                        variant={r.severity === 'critical'
                          ? 'error'
                          : r.severity === 'high'
                          ? 'warning'
                          : 'secondary'}
                      >
                        {r.severity}
                      </Badge>
                      <span className='text-red-400'>⚠ {t('mqtt.blockedMessage', { name: r.name })}</span>
                    </div>
                  ))
                )}
            </div>
          </CardContent>
        </Card>
      )}

      {addOpen && (
        <Card className='border-blue-500/30'>
          <CardContent className='space-y-3 py-4'>
            <div className='grid grid-cols-3 gap-3'>
              <div>
                <span className='text-[11px] text-gray-500'>{t('mqtt.colSeverity')}</span>
                <Select
                  className='mt-0.5 w-full'
                  value={form.severity}
                  onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
                >
                  <option value='critical'>{t('mqtt.sevOptCritical')}</option>
                  <option value='high'>{t('mqtt.sevOptHigh')}</option>
                  <option value='medium'>{t('mqtt.sevOptMedium')}</option>
                  <option value='low'>{t('mqtt.sevOptLow')}</option>
                </Select>
              </div>
              <div>
                <span className='text-[11px] text-gray-500'>{t('mqtt.colName')}</span>
                <input
                  className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white'
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder={t('mqtt.namePlaceholderHint')}
                />
              </div>
              <div>
                <span className='text-[11px] text-gray-500'>{t('mqtt.colPattern')} (regex)</span>
                <input
                  className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono'
                  value={form.pattern}
                  onChange={(e) => setForm((f) => ({ ...f, pattern: e.target.value }))}
                  placeholder={t('mqtt.patternPlaceholderHint')}
                />
              </div>
            </div>
            <div className='flex justify-end gap-2'>
              <Button
                variant='ghost'
                onClick={() => {
                  setAddOpen(false);
                  setEditIdx(null);
                }}
              >
                {t('mqtt.cancelButton')}
              </Button>
              <Button
                variant='primary'
                onClick={editIdx !== null ? handleSaveEdit : handleAdd}
                loading={saveMut.isPending}
              >
                {editIdx !== null ? t('mqtt.saveConfigButton') : t('mqtt.addButton')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Modal */}
      <Modal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setImportText('');
        }}
        title={t('mqtt.importModalTitle')}
        footer={
          <>
            <Button
              variant='secondary'
              onClick={() => {
                setImportOpen(false);
                setImportText('');
              }}
            >
              {t('mqtt.cancelButton')}
            </Button>
            <Button
              variant='primary'
              onClick={handleImport}
              loading={saveMut.isPending}
              disabled={!importText.trim()}
            >
              {importMode === 'merge' ? t('mqtt.mergeAction') : t('mqtt.replaceAction')}
            </Button>
          </>
        }
      >
        <div className='space-y-3'>
          <div>
            <Label>{t('mqtt.modeLabel')}</Label>
            <div className='flex gap-2 mt-1'>
              <button
                onClick={() => setImportMode('replace')}
                className={`px-3 py-1.5 rounded text-xs ${
                  importMode === 'replace'
                    ? 'bg-red-900/30 text-red-300 border border-red-500/40'
                    : 'bg-gray-900 text-gray-500 border border-gray-800'
                }`}
              >
                {t('mqtt.replaceAllOption')}
              </button>
              <button
                onClick={() => setImportMode('merge')}
                className={`px-3 py-1.5 rounded text-xs ${
                  importMode === 'merge'
                    ? 'bg-blue-900/30 text-blue-300 border border-blue-500/40'
                    : 'bg-gray-900 text-gray-500 border border-gray-800'
                }`}
              >
                {t('mqtt.mergeOption')}
              </button>
            </div>
          </div>
          <div>
            <Label>{t('mqtt.jsonFileLabel')}</Label>
            <input
              type='file'
              accept='.json,application/json'
              onChange={handleImportFile}
              className='mt-1 block w-full text-xs text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-gray-800 file:text-gray-200 hover:file:bg-gray-700'
            />
          </div>
          <div>
            <Label>{t('mqtt.orPasteJsonLabel')}</Label>
            <Textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              rows={8}
              className='font-mono text-xs'
              placeholder='[{"severity":"high","name":"...","pattern":"..."}]'
            />
          </div>
          <p className='text-[11px] text-gray-500'>
            {t('mqtt.formatHintPre')} <code className='font-mono'>severity</code>{' '}
            {t('mqtt.formatHintMid')}
            <code className='font-mono mx-1'>name</code>, <code className='font-mono'>pattern</code>
            {' '}
            {t('mqtt.formatHintPost')}
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default function MqttPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '' });
  const { data: meshPeers = [] } = useMeshPeers({ includeLocal: true });
  const labelOf = (key: string) => {
    const peer = meshPeers.find((p) => peerMatchesKey(p, key));
    return peer ? resolveNodeDisplayLabel(key, meshPeers, '') : '';
  };

  // ─── Queries ──────────────────────────────────────────────────────────────

  const { data: config, refetch: refetchConfig } = useQuery<MqttConfig | null>({
    queryKey: ['mqtt-config'],
    queryFn: () => api.mcp('mqtt.status', { action: 'config_get' }),
    staleTime: 30_000,
  });

  const { data: status, refetch: refetchStatus } = useQuery<MqttStatus>({
    queryKey: ['mqtt-status'],
    queryFn: () => api.mcp('mqtt.status', { action: 'status' }),
    staleTime: 30_000,
  });

  const { data: clientsData, error: clientsError, refetch: refetchClients } = useQuery<
    { ok: boolean; data: MqttClient[]; meta?: unknown }
  >({
    queryKey: ['mqtt-client-inventory'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; data: MqttClient[]; error?: string }>('mqtt.client_inventory', {});
      if (!r.ok) throw new Error(r.error || 'MQTT_CLIENT_INVENTORY_UNAVAILABLE');
      return r;
    },
    staleTime: 10_000,
    refetchInterval: 15_000,
  });

  const { data: usersData, refetch: refetchUsers } = useQuery<
    { ok: boolean; users?: MqttUser[]; data?: MqttUser[] }
  >({
    queryKey: ['mqtt-users'],
    queryFn: () => api.mcp('mqtt.users', { action: 'list' }),
    staleTime: 60_000,
  });
  const users = usersData?.users ?? usersData?.data ?? [];

  const { data: subsData, refetch: refetchSubs } = useQuery<
    { ok?: boolean; data?: MqttSubRow[]; total?: number }
  >({
    queryKey: ['mqtt-subscriptions'],
    queryFn: () => api.mcp('mqtt.status', { action: 'subscriptions' }),
    staleTime: 15_000,
    refetchInterval: 20_000,
  });
  const { data: bridgesData, error: bridgesError, refetch: refetchBridges } = useQuery<
    { ok?: boolean; bridges?: MqttBridgeRow[]; inbound?: MqttClientRow[]; total?: number }
  >({
    queryKey: ['mqtt-bridges'],
    queryFn: async () => {
      const r = await api.mcp<{ok: boolean; bridges?: MqttBridgeRow[]; inbound?: MqttClientRow[]; error?: string}>('mqtt.bridge_state', {});
      if (!r.ok) throw new Error(r.error || 'MQTT_DIAGNOSTICS_UNAVAILABLE');
      return r;
    },
    staleTime: 15_000,
    refetchInterval: 20_000,
  });
  const clients: MqttClientRow[] = (clientsData?.data ?? []).map((c) => ({
    ...c,
    ...bridgesData?.inbound?.find((b) => b.clientid === mqttClientId(c as unknown as Record<string, unknown>)),
    connected: clientsError ? undefined : c.connected,
    clientid: mqttClientId(c as unknown as Record<string, unknown>),
  }));
  const subs = subsData?.data ?? [];
  const bridges = bridgesData?.bridges ?? [];

  // ─── Mutations ────────────────────────────────────────────────────────────

  const updateConfigMutation = useMutation({
    mutationFn: async (data: Partial<MqttConfig>) => {
      const body: Record<string, string> = {};
      if (data.emqx_url) body.emqx_url = data.emqx_url;
      if (data.emqx_api_key) body.emqx_api_key = data.emqx_api_key;
      if (data.broker_url) body.broker_url = data.broker_url;
      if (data.broker_host) body.broker_host = data.broker_host;
      return api.mcp('mqtt.status', { action: 'config_set', ...body });
    },
    onSuccess: () => {
      toast.success(t('mqtt.configSavedToast'));
      qc.invalidateQueries({ queryKey: ['mqtt-config'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const createUserMutation = useMutation({
    mutationFn: async ({ username, password }: { username: string; password: string }) => {
      return api.mcp('mqtt.users', { action: 'create', username, password });
    },
    onSuccess: () => {
      toast.success(t('mqtt.userCreatedToast'));
      qc.invalidateQueries({ queryKey: ['mqtt-users'] });
      setShowCreateUser(false);
      setNewUser({ username: '', password: '' });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (username: string) => {
      return api.mcp('mqtt.users', { action: 'delete', username });
    },
    onSuccess: () => {
      toast.success(t('mqtt.userDeletedToast'));
      qc.invalidateQueries({ queryKey: ['mqtt-users'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleDeleteUser = async (username: string) => {
    if (await themedConfirm(t('mqtt.deleteUserConfirm', { username }))) {
      deleteUserMutation.mutate(username);
    }
  };

  const handleCreateUser = () => {
    if (!newUser.username || !newUser.password) {
      toast.error(t('mqtt.usernamePasswordRequired'));
      return;
    }
    createUserMutation.mutate(newUser);
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className='space-y-6'>
      {/* Header */}
      <PageHeader
        title={t('mqtt.pageTitle')}
        description={t('mqtt.pageDescription')}
        icon={<Radio size={24} />}
        actions={
          <div className='flex items-center gap-2'>
            <Button
              variant='secondary'
              size='sm'
              icon={<RefreshCw size={14} />}
              onClick={() => {
                refetchConfig();
                refetchStatus();
                refetchClients();
                refetchUsers();
                void refetchSubs();
                void refetchBridges();
              }}
              loading={updateConfigMutation.isPending || createUserMutation.isPending}
            >
              {t('mqtt.refreshButton')}
            </Button>
          </div>
        }
      />

      {(clientsError || bridgesError) && <p role='alert' className='text-amber-300'>{t('mqtt.verificationUnknown')}: {String(clientsError?.message || bridgesError?.message)}</p>}
      {/* Stats */}
      <StatsGrid columns={4}>
        <StatCard
          label={t('mqtt.statBroker')}
          value={status?.emqx_running ? t('mqtt.statusOnline') : t('mqtt.statusOffline')}
          sub={status?.systemd || '—'}
          icon={Server}
          variant={status?.emqx_running ? 'success' : 'error'}
        />
        <StatCard
          label={t('mqtt.statConfig')}
          value={config?.configured ? t('mqtt.statusDone') : t('mqtt.statusSetup')}
          icon={Code}
          variant={config?.configured ? 'success' : 'warning'}
        />
        <StatCard
          label={t('mqtt.statClients')}
          value={clients.length}
          icon={Users}
        />
        <StatCard
          label={t('mqtt.statSubs', 'Odběry')}
          value={subsData?.total ?? subs.length}
          icon={Radio}
        />
      </StatsGrid>

      {/* Tabs */}
      <Tabs defaultTab={activeTab} onTabChange={setActiveTab}>
        <TabList>
          <Tab id='overview' icon={<Server size={14} />}>{t('mqtt.tabOverview')}</Tab>
          <Tab id='relations' icon={<Radio size={14} />}>{t('mqtt.tabRelations', 'Vztahy')}</Tab>
          <Tab id='subs' icon={<Radio size={14} />}>{t('mqtt.tabSubs', 'Odběry')}</Tab>
          <Tab id='config' icon={<Code size={14} />}>{t('mqtt.tabConfig')}</Tab>
          <Tab id='clients' icon={<Users size={14} />}>{t('mqtt.tabClients')}</Tab>
          <Tab id='users' icon={<Shield size={14} />}>{t('mqtt.tabUsers')}</Tab>
          <Tab id='acl' icon={<Shield size={14} />}>{t('mqtt.tabAclRules')}</Tab>
        </TabList>

        <TabPanel id='relations'>
          <MqttRelations clients={clients} subs={subs} bridges={bridges} />
        </TabPanel>

        <TabPanel id='subs'>
          <MqttSubscriptions subs={subs} clients={clients} />
        </TabPanel>

        {/* Overview Tab */}
        <TabPanel id='overview'>
          <div className='mb-6'>
            <MqttRelations clients={clients} subs={subs} bridges={bridges} />
          </div>
          <div className='grid gap-6 md:grid-cols-2'>
            <Card>
              <CardHeader>
                <CardTitle icon={Server} className='text-sm'>{t('mqtt.emqxBrokerCard')}</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <span className='text-xs text-gray-500'>{t('mqtt.serviceStatusLabel')}</span>
                  <ServiceBadge running={status?.emqx_running ?? false} />
                </div>

                <div className='flex items-center justify-between'>
                  <span className='text-xs text-gray-500'>{t('mqtt.restApiLabel')}</span>
                  <code className='font-mono text-xs text-gray-400'>
                    {status?.emqx_url || '—'}
                  </code>
                </div>

                <div className='flex items-center justify-between'>
                  <span className='text-xs text-gray-500'>{t('mqtt.mqttPortLabel')}</span>
                  <code className='font-mono text-xs text-gray-400'>
                    {status?.broker_url || '—'}
                  </code>
                </div>

                <div className='p-3 bg-blue-950/20 border border-blue-500/20 rounded-xl'>
                  <p className='text-xs text-blue-300'>
                    <span className='font-semibold'>{t('mqtt.noteLabel')}</span>{' '}
                    {t('mqtt.sshTunnelHint')}
                  </p>
                  <code className='block mt-2 text-[11px] font-mono text-blue-400 bg-black/30 p-2 rounded'>
                    ssh -L 18083:127.0.0.1:18083 user@server
                  </code>
                  <p className='text-[11px] text-blue-400/80 mt-1'>
                    {t('mqtt.afterTunnelHint')}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle icon={Globe} className='text-sm'>{t('mqtt.publicAccessCard')}</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <span className='text-xs text-gray-500'>{t('mqtt.brokerUrlLabel')}</span>
                  <code className='font-mono text-xs text-gray-400'>
                    {config?.broker_url || '—'}
                  </code>
                </div>

                <div className='flex items-center justify-between'>
                  <span className='text-xs text-gray-500'>{t('mqtt.hostnameLabel')}</span>
                  <span className='text-sm text-gray-300'>
                    {config?.broker_host || '—'}
                  </span>
                </div>

                <div className='p-3 bg-amber-950/20 border border-amber-500/20 rounded-xl'>
                  <p className='text-xs text-amber-300'>
                    <span className='font-semibold'>{t('mqtt.securityLabel')}</span>{' '}
                    {t('mqtt.tlsSecurityHint')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className='mt-6 grid gap-6 md:grid-cols-2'>
            <Card>
              <CardHeader>
                <CardTitle icon={Key} className='text-sm'>{t('mqtt.apiKeyCard')}</CardTitle>
              </CardHeader>
              <CardContent>
                {config?.configured
                  ? (
                    <div className='space-y-3'>
                      <div className='flex items-center gap-2'>
                        <span className='text-xs text-gray-500'>{t('mqtt.emqxApiKeyLabel')}</span>
                        <KeyBadge apiKey={config.emqx_api_key} />
                      </div>
                      <p className='text-xs text-gray-500'>
                        {t('mqtt.apiKeyStorageHint')}
                      </p>
                    </div>
                  )
                  : (
                    <EmptyState
                      icon={<Key size={24} />}
                      title={t('mqtt.notSetTitle')}
                      description={t('mqtt.setApiKeyHint')}
                    />
                  )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle icon={Terminal} className='text-sm'>{t('mqtt.statsCard')}</CardTitle>
              </CardHeader>
              <CardContent className='space-y-3'>
                {status?.stats
                  ? (
                    <>
                      <div className='grid grid-cols-2 gap-2'>
                        <div className='p-2 bg-gray-900/30 rounded'>
                          <div className='text-[10px] text-gray-500 uppercase'>{t('mqtt.connectionsLabel')}</div>
                          <div className='text-lg font-bold text-white'>
                            {typeof status.stats === 'object' && status.stats
                              ? (
                                (status.stats as any).nodes?.[0]?.connections ?? '—'
                              )
                              : '—'}
                          </div>
                        </div>
                        <div className='p-2 bg-gray-900/30 rounded'>
                          <div className='text-[10px] text-gray-500 uppercase'>{t('mqtt.messagesPerSecLabel')}</div>
                          <div className='text-lg font-bold text-white'>
                            {typeof status.stats === 'object' && status.stats
                              ? (
                                (status.stats as any).messages?.received ?? '—'
                              )
                              : '—'}
                          </div>
                        </div>
                      </div>
                      <p className='text-xs text-gray-500'>
                        {t('mqtt.lastUpdateLabel', { time: new Date().toLocaleTimeString('cs-CZ') })}
                      </p>
                    </>
                  )
                  : (
                    <EmptyState
                      icon={<Terminal size={24} />}
                      title={t('mqtt.noStatsTitle')}
                      description={t('mqtt.noStatsDescription')}
                    />
                  )}
              </CardContent>
            </Card>
          </div>
        </TabPanel>

        {/* Config Tab */}
        <TabPanel id='config'>
          <ConfigForm
            config={config ?? null}
            onSave={(data) => updateConfigMutation.mutate(data)}
          />
        </TabPanel>

        {/* Clients Tab */}
        <TabPanel id='clients'>
          <div className='flex items-center justify-between mb-4'>
            <div>
              <h3 className='text-sm font-medium text-white'>{t('mqtt.connectedClientsTitle')}</h3>
              <p className='text-xs text-gray-500'>{t('mqtt.connectedClientsDescription')}</p>
            </div>
            <Button
              variant='secondary'
              size='sm'
              icon={<RefreshCw size={14} />}
              onClick={() => void refetchClients()}
            >
              {t('mqtt.refreshButton')}
            </Button>
          </div>

          {clientsData?.data && clientsData.data.length > 0
            ? (
              <div className='rounded-xl border border-gray-800 overflow-hidden bg-gray-900/20'>
                <table className='w-full text-left text-sm'>
                  <thead className='bg-gray-900/40 border-b border-gray-800'>
                    <tr>
                      <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                        {t('mqtt.colFamiliar', 'Jméno')}
                      </th>
                      <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                        {t('mqtt.lastContact', 'Poslední kontakt')}
                      </th>
                      <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                        {t('mqtt.colClientId')}
                      </th>
                      <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                        {t('mqtt.colIpAddress')}
                      </th>
                      <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                        {t('mqtt.colSubs', 'Odběry')}
                      </th>
                      <th className='px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                        {t('mqtt.colProtocol')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-800'>
                    {clients.map((client) => {
                      const id = mqttClientId(client);
                      const iso = clientContactIso(client);
                      const ageMs = iso ? Date.now() - Date.parse(iso) : NaN;
                      let ageLabel = '—';
                      if (Number.isFinite(ageMs)) {
                        const s = Math.max(0, Math.floor(ageMs / 1000));
                        ageLabel = s < 15
                          ? t('mqtt.contactNow', 'teď')
                          : s < 60
                          ? t('mqtt.contactSeconds', '{{n}}s', { n: s })
                          : s < 3600
                          ? t('mqtt.contactMinutes', '{{n}} min', { n: Math.floor(s / 60) })
                          : s < 86400
                          ? t('mqtt.contactHours', '{{n}} h', { n: Math.floor(s / 3600) })
                          : t('mqtt.contactDays', '{{n}} d', { n: Math.floor(s / 86400) });
                      }
                      return (
                      <tr key={id} className='hover:bg-gray-800/30 transition-colors'>
                        <td className='px-4 py-3'>
                          <div className='text-sm font-medium text-white'>
                            {clientFamiliarName(client, labelOf)}
                          </div>
                          {client.username
                            ? (
                              <div className='font-mono text-[10px] text-gray-600 truncate max-w-[220px]'>
                                {client.username}
                              </div>
                            )
                            : null}
                        </td>
                        <td className='px-4 py-3 text-gray-300'>
                          <div>{ageLabel}</div>
                          <div className='text-[10px] text-gray-600'>
                            {iso ? new Date(iso).toLocaleString() : '—'}
                          </div>
                        </td>
                        <td className='px-4 py-3 font-mono text-xs text-blue-400 max-w-[280px] truncate' title={id}>
                          {id}
                        </td>
                        <td className='px-4 py-3 text-gray-400'>
                          {client.ip_address}
                        </td>
                        <td className='px-4 py-3 text-gray-300'>
                          {subs.filter((s) => s.clientid === id).length ||
                            client.subscriptions_cnt ||
                            0}
                        </td>
                        <td className='px-4 py-3'>
                          <Badge variant='secondary' className='text-[10px]'>
                            {client.protocol}
                          </Badge>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
            : (
              <EmptyState
                icon={<Users size={24} />}
                title={t('mqtt.noClientsTitle')}
                description={t('mqtt.noClientsDescription')}
              />
            )}
        </TabPanel>

        {/* Users Tab */}
        <TabPanel id='acl'>
          <AclPatternsSection />
        </TabPanel>

        <TabPanel id='users'>
          <div className='flex items-center justify-between mb-4'>
            <div>
              <h3 className='text-sm font-medium text-white'>{t('mqtt.mqttUsersTitle')}</h3>
              <p className='text-xs text-gray-500'>{t('mqtt.usersManagementDescription')}</p>
            </div>
            <div className='flex items-center gap-2'>
              <Button
                variant='secondary'
                size='sm'
                icon={<RefreshCw size={14} />}
                onClick={() => void refetchUsers()}
              >
                {t('mqtt.refreshButton')}
              </Button>
              <Button
                variant='primary'
                size='sm'
                icon={<Plus size={14} />}
                onClick={() => setShowCreateUser(true)}
              >
                {t('mqtt.newUserButton')}
              </Button>
            </div>
          </div>

          <UserTable
            users={users}
            onDelete={handleDeleteUser}
            onCreate={() => setShowCreateUser(true)}
          />
        </TabPanel>
      </Tabs>

      {/* Create User Modal */}
      <Modal
        open={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        title={t('mqtt.newUserModalTitle')}
        footer={
          <>
            <Button variant='secondary' onClick={() => setShowCreateUser(false)}>
              {t('mqtt.cancelButton')}
            </Button>
            <Button
              variant='primary'
              loading={createUserMutation.isPending}
              onClick={handleCreateUser}
            >
              {t('mqtt.createButton')}
            </Button>
          </>
        }
      >
        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='username'>{t('mqtt.usernameLabel')}</Label>
            <Input
              id='username'
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              placeholder={t('mqtt.usernamePlaceholderHint')}
              autoFocus
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='password'>{t('mqtt.passwordLabel')}</Label>
            <Input
              id='password'
              type='password'
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              placeholder='••••••••'
            />
            <p className='text-[10px] text-gray-500'>
              {t('mqtt.passwordMinLengthHint')}
            </p>
          </div>

          <div className='p-3 bg-blue-950/20 border border-blue-500/20 rounded-xl'>
            <p className='text-xs text-blue-300'>
              <span className='font-semibold'>{t('mqtt.importantLabel')}</span>{' '}
              {t('mqtt.aclSetupHintPre')}{' '}
              <code className='font-mono text-blue-400'>mqtt_users</code>
              {' '}{t('mqtt.aclSetupHintPost')}
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
